import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { MailIcon, MapPinIcon, PhoneIcon, PlusIcon, TruckIcon } from 'lucide-react';
type SupplierForm = {
  name: string;
  contactPerson: string;
  phone: string;
  email: string;
  address: string;
};
const initialForm: SupplierForm = {
  name: '',
  contactPerson: '',
  phone: '',
  email: '',
  address: ''
};
export function Suppliers() {
  const {
    suppliers,
    addSupplier,
    updateSupplier
  } = useData();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<SupplierForm>(initialForm);
  const [error, setError] = useState('');
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('Supplier name is required.');
      return;
    }
    const contactDetails = {
      ...(form.contactPerson.trim() ? {
        contactPerson: form.contactPerson.trim()
      } : {}),
      ...(form.phone.trim() ? {
        phone: form.phone.trim()
      } : {}),
      ...(form.email.trim() ? {
        email: form.email.trim()
      } : {}),
      ...(form.address.trim() ? {
        address: form.address.trim()
      } : {})
    };
    addSupplier({
      name: form.name.trim(),
      ...contactDetails,
      status: 'Active'
    });
    setForm(initialForm);
    setError('');
    setShowForm(false);
  };
  const toggleSupplierStatus = (supplierId: string, status: 'Active' | 'Inactive') => {
    updateSupplier(supplierId, {
      status: status === 'Active' ? 'Inactive' : 'Active'
    });
  };
  return <div className="space-y-5" data-id="element-5487" data-mp="a9hc99zrnvqn">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-5488" data-mp="a9r5d6g3yj0k">
        <div data-id="element-5489" data-mp="59ux2v0do5f7">
          <h2 className="text-xl font-bold text-slate-950" data-id="element-5490" data-mp="5bfwoz1juejx">Suppliers</h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-5491" data-mp="of9hc7s7jw24">
            Supplier name is required. Contact details are optional and remain
            available on each linked delivery batch.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700" data-id="element-5492" data-mp="ff4d26ib9ds6">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-5493" data-mp="ke7kfxlrjw3b" /> Add supplier
        </Button>
      </div>
      {showForm && <form onSubmit={submit} className="grid grid-cols-1 gap-4 rounded-xl border border-teal-200 bg-teal-50/50 p-5 md:grid-cols-2" data-id="element-5494" data-mp="183ieyxcisu90">
          <div className="flex justify-between md:col-span-2" data-id="element-5495" data-mp="y5m13xs77l77">
            <div data-id="element-5496" data-mp="y770c0pgjsbe">
              <h3 className="font-bold text-slate-900" data-id="element-5497" data-mp="133noguwnq6oh">New supplier</h3>
              <p className="mt-1 text-xs text-slate-500" data-id="element-5498" data-mp="135qkbvkhqu14">
                Only the supplier name is mandatory.
              </p>
            </div>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm font-semibold text-slate-500 hover:text-slate-900" data-id="element-5499" data-mp="3nuek7t12596t">
              Cancel
            </button>
          </div>
          {error && <p role="alert" className="text-sm text-red-600 md:col-span-2" data-id="element-5500" data-mp="3pb1ufn033j5y">
              {error}
            </p>}
          <Input label="Supplier name" required value={form.name} onChange={event => setForm({
        ...form,
        name: event.target.value
      })} data-id="element-5501" data-mp="3kda3owb6e33c" />
          <Input label="Contact person (optional)" value={form.contactPerson} onChange={event => setForm({
        ...form,
        contactPerson: event.target.value
      })} data-id="element-5502" data-mp="36c62kibp7gva" />
          <Input label="Phone (optional)" type="tel" value={form.phone} onChange={event => setForm({
        ...form,
        phone: event.target.value
      })} data-id="element-5503" data-mp="2dtsdb74fh37t" />
          <Input label="Email (optional)" type="email" value={form.email} onChange={event => setForm({
        ...form,
        email: event.target.value
      })} data-id="element-5504" data-mp="2rjdoequgofr1" />
          <Input label="Address (optional)" className="md:col-span-2" value={form.address} onChange={event => setForm({
        ...form,
        address: event.target.value
      })} data-id="element-5505" data-mp="3598qd4uw21v9" />
          <div className="flex justify-end md:col-span-2" data-id="element-5506" data-mp="3k6hw53kxv86i">
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-id="element-5507" data-mp="1kuf4v762p9ia">
              Save supplier
            </Button>
          </div>
        </form>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-id="element-5508" data-mp="1pu1opzgepo02">
        {suppliers.map(supplier => <article key={supplier.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-5509" data-mp="6zovkwgtsnoy">
            <div className="flex items-start gap-3" data-id="element-5510" data-mp="3ub7yhak98eb9">
              <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700" data-id="element-5511" data-mp="3ubpse0jg2gg0">
                <TruckIcon className="h-5 w-5" data-id="element-5512" data-mp="3pdobnm9hqt7p" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-5513" data-mp="pwzw0ke71n5z">
                <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-5514" data-mp="pwq42r9l95xn">
                  <h3 className="font-bold text-slate-900" data-id="element-5515" data-mp="lwzdoonz4rtn">{supplier.name}</h3>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${supplier.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-5516" data-mp="gvs5iohx5qmg">
                    {supplier.status}
                  </span>
                </div>
                {supplier.contactPerson && <p className="mt-2 text-sm font-medium text-slate-700" data-id="element-5517" data-mp="2wq298hc01umf">
                    {supplier.contactPerson}
                  </p>}
                <div className="mt-2 space-y-1 text-sm text-slate-500" data-id="element-5518" data-mp="332wm9v84kzza">
                  {supplier.phone && <p className="flex items-center gap-2" data-id="element-5519" data-mp="2p3olwz087lti">
                      <PhoneIcon className="h-3.5 w-3.5" data-id="element-5520" data-mp="2u2rrqlo15w2j" /> {supplier.phone}
                    </p>}
                  {supplier.email && <p className="flex items-center gap-2" data-id="element-5521" data-mp="2fcai4f18rzuk">
                      <MailIcon className="h-3.5 w-3.5" data-id="element-5522" data-mp="2k6579xzm9711" /> {supplier.email}
                    </p>}
                  {supplier.address && <p className="flex items-start gap-2" data-id="element-5523" data-mp="2afadm8k1j55k">
                      <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-5524" data-mp="2afk2fstcn69o" />
                      {supplier.address}
                    </p>}
                  {!supplier.contactPerson && !supplier.phone && !supplier.email && !supplier.address && <p data-id="element-5525" data-mp="1ht2hob6cb52p">No contact details recorded.</p>}
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => toggleSupplierStatus(supplier.id, supplier.status)} className="mt-4" data-id="element-5526" data-mp="1cv0ylq8wz5r8">
                  {supplier.status === 'Active' ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            </div>
          </article>)}
        {!suppliers.length && <div className="col-span-full rounded-xl border border-dashed border-slate-300 py-14 text-center text-sm text-slate-500" data-id="element-5527" data-mp="2ag6g1xaxgl5i">
            No suppliers yet. Add a supplier before receiving a delivery or
            creating a purchase order.
          </div>}
      </div>
    </div>;
}